import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, Thermometer, ArrowLeft, Calendar, User, MapPin, CheckCircle, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const TemperatureAlerts = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [loading, setLoading] = useState(true);
  const [showRead, setShowRead] = useState(false);

  useEffect(() => {
    fetchLocations();
  }, []);

  useEffect(() => {
    if (selectedLocation) {
      fetchAlerts(selectedLocation);
    }
  }, [selectedLocation]);

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API}/admin/locations`);
      setLocations(response.data);
      if (response.data.length > 0) {
        setSelectedLocation(response.data[0].id);
      }
    } catch (error) {
      toast.error('Fehler beim Laden der Standorte');
    }
  };

  const fetchAlerts = async (locationId) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/temperature/alerts?location_id=${locationId}`);
      
      // Fetch employee names for each alert
      const alertsWithNames = await Promise.all(
        response.data.map(async (alert) => {
          if (alert.recorded_by) {
            try {
              const userRes = await axios.get(`${API}/users/${alert.recorded_by}`);
              return { ...alert, recorded_by_name: userRes.data.name };
            } catch {
              return { ...alert, recorded_by_name: 'Unbekannt' };
            }
          }
          return { ...alert, recorded_by_name: 'Unbekannt' };
        })
      );
      
      setAlerts(alertsWithNames);
    } catch (error) {
      toast.error('Fehler beim Laden der Alarme');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const markAsRead = async (alertId) => {
    try {
      await axios.put(`${API}/temperature/alarms/${alertId}/read`);
      setAlerts(prev => prev.map(a => a.id === alertId ? { ...a, is_read: true } : a));
      toast.success('Alarm als gelesen markiert');
    } catch (error) {
      toast.error('Fehler beim Markieren');
    }
  };

  const markAllAsRead = async () => {
    try {
      const res = await axios.put(
        `${API}/temperature/alarms/mark-all-read${selectedLocation ? `?location_id=${selectedLocation}` : ''}`
      );
      setAlerts(prev => prev.map(a => ({ ...a, is_read: true })));
      toast.success(`${res.data.count || 'Alle'} Alarme als gelesen markiert`);
    } catch (error) {
      toast.error('Fehler beim Markieren aller Alarme');
    }
  };

  const unreadAlerts = alerts.filter(a => !a.is_read);
  const readAlerts = alerts.filter(a => a.is_read);
  const displayedAlerts = showRead ? alerts : unreadAlerts;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate('/admin/dashboard')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Zurück
          </Button>
        </div>

        <div>
          <h1 className="text-3xl font-bold" style={{fontFamily: 'Manrope, sans-serif'}}>
            Temperatur-Alarme
          </h1>
          <p className="text-muted-foreground mt-2">
            Übersicht aller Temperaturabweichungen
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              Standort auswählen
            </CardTitle>
          </CardHeader>
          <CardContent>
            <select
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
              className="flex h-10 w-full max-w-md rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {locations.map(location => (
                <option key={location.id} value={location.id}>{location.name}</option>
              ))}
            </select>
          </CardContent>
        </Card>

        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : alerts.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Thermometer className="mx-auto h-12 w-12 text-green-500" />
              <p className="mt-4 text-lg font-medium text-green-700">Keine Alarme vorhanden</p>
              <p className="text-sm text-muted-foreground mt-2">
                Alle Temperaturmessungen sind im Sollbereich.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-500" />
                {unreadAlerts.length} ungelesene{unreadAlerts.length !== 1 ? '' : 'r'} Alarm{unreadAlerts.length !== 1 ? 'e' : ''}
              </h2>
              <div className="flex items-center gap-2">
                {unreadAlerts.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={markAllAsRead}
                    className="text-green-700 border-green-300 hover:bg-green-50 dark:hover:bg-green-900/20"
                    data-testid="mark-all-read-btn"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Alle als gelesen ({unreadAlerts.length})
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowRead(!showRead)}
                >
                  <Eye className="h-4 w-4 mr-2" />
                  {showRead ? 'Nur ungelesene' : `Alle anzeigen (${alerts.length})`}
                </Button>
              </div>
            </div>

            <div className="grid gap-4">
              {displayedAlerts.map((alert, index) => (
                <Card key={index} className={`${alert.is_read ? 'border-gray-200 bg-gray-50/50' : 'border-red-200 bg-red-50/50'}`}>
                  <CardContent className="p-4">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className={`p-3 rounded-lg ${alert.is_read ? 'bg-gray-100' : 'bg-red-100'}`}>
                          {alert.is_read ? (
                            <CheckCircle className="h-6 w-6 text-gray-500" />
                          ) : (
                            <AlertTriangle className="h-6 w-6 text-red-600" />
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-lg">{alert.measuring_point}</h3>
                            {alert.is_read && (
                              <Badge variant="outline" className="bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">Gelesen</Badge>
                            )}
                          </div>
                          <p className={`text-sm mt-1 ${alert.is_read ? 'text-gray-600' : 'text-red-700'}`}>{alert.alarm_reason}</p>
                          <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-4 w-4" />
                              {formatDate(alert.created_at)}
                            </span>
                            <span className="flex items-center gap-1">
                              <User className="h-4 w-4" />
                              {alert.recorded_by_name}
                            </span>
                          </div>
                          {alert.comment && (
                            <p className="mt-2 text-sm bg-white dark:bg-gray-900/50 p-2 rounded border">
                              <strong>Kommentar:</strong> {alert.comment}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <Badge variant={alert.is_read ? "secondary" : "destructive"} className="text-lg px-4 py-2">
                          {alert.temperature}°C
                        </Badge>
                        {!alert.is_read && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => markAsRead(alert.id)}
                            className="mt-2"
                          >
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Als gelesen markieren
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};
